import React, { useState, useEffect, useCallback } from "react";
import {
    BarChart3,
    Calendar,
    ChevronDown,
    Download,
    DollarSign,
    TrendingUp,
    TrendingDown,
    Users,
    FileText,
    CheckCircle,
    AlertCircle,
    Loader2,
    RefreshCw,
    Filter,
    ArrowUpRight,
    Clock,
    Building2,
    PieChart,
    Target,
    Activity
} from "lucide-react";
import {
    fetchProductionReport,
    fetchPipelineReport,
    fetchFunnelReport,
    fetchLoanOfficerReport,
    fetchBookedLoans,
    fetchReportingPeriods,
    closeReportingPeriod,
    exportReport
} from "../api/reports";

// Date range presets
const DATE_PRESETS = [
    { label: "This Month", value: "this_month" },
    { label: "Last Month", value: "last_month" },
    { label: "This Quarter", value: "this_quarter" },
    { label: "Last Quarter", value: "last_quarter" },
    { label: "Year to Date", value: "ytd" },
    { label: "Last 12 Months", value: "last_12_months" },
    { label: "Custom", value: "custom" }
];

// Helper to calculate date ranges
const getDateRange = (preset) => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    switch (preset) {
        case "this_month":
            return {
                start: startOfMonth.toISOString().split('T')[0],
                end: endOfMonth.toISOString().split('T')[0]
            };
        case "last_month":
            const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
            return {
                start: lastMonthStart.toISOString().split('T')[0],
                end: lastMonthEnd.toISOString().split('T')[0]
            };
        case "this_quarter":
            const quarter = Math.floor(now.getMonth() / 3);
            const quarterStart = new Date(now.getFullYear(), quarter * 3, 1);
            const quarterEnd = new Date(now.getFullYear(), (quarter + 1) * 3, 0);
            return {
                start: quarterStart.toISOString().split('T')[0],
                end: quarterEnd.toISOString().split('T')[0]
            };
        case "last_quarter":
            const lastQ = Math.floor(now.getMonth() / 3) - 1;
            const year = lastQ < 0 ? now.getFullYear() - 1 : now.getFullYear();
            const actualQ = lastQ < 0 ? 3 : lastQ;
            const lastQStart = new Date(year, actualQ * 3, 1);
            const lastQEnd = new Date(year, (actualQ + 1) * 3, 0);
            return {
                start: lastQStart.toISOString().split('T')[0],
                end: lastQEnd.toISOString().split('T')[0]
            };
        case "ytd":
            return {
                start: `${now.getFullYear()}-01-01`,
                end: now.toISOString().split('T')[0]
            };
        case "last_12_months":
            const twelveMonthsAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
            return {
                start: twelveMonthsAgo.toISOString().split('T')[0],
                end: now.toISOString().split('T')[0]
            };
        default:
            return {
                start: startOfMonth.toISOString().split('T')[0],
                end: endOfMonth.toISOString().split('T')[0]
            };
    }
};

// Format currency
const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "$0";
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
};

// Format percentage
const formatPercent = (value) => {
    if (!value && value !== 0) return "0%";
    return `${value.toFixed(1)}%`;
};

// Metric Card Component
const MetricCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = "sky" }) => {
    const colorClasses = {
        sky: "from-sky-500 to-blue-600",
        emerald: "from-emerald-500 to-teal-600",
        amber: "from-amber-500 to-orange-600",
        purple: "from-purple-500 to-indigo-600",
        rose: "from-rose-500 to-pink-600"
    };

    return (
        <div className="dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-5 relative overflow-hidden">
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <p className="text-sm dark:text-slate-400 text-slate-500 font-medium">{title}</p>
                    <p className="text-2xl font-bold dark:text-white text-slate-900 mt-1">{value}</p>
                    {subtitle && (
                        <p className="text-xs dark:text-slate-500 text-slate-400 mt-1">{subtitle}</p>
                    )}
                    {trend && (
                        <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${trend === "up" ? "text-emerald-500" : "text-red-400"
                            }`}>
                            {trend === "up" ? (
                                <TrendingUp className="w-3 h-3" />
                            ) : (
                                <TrendingDown className="w-3 h-3" />
                            )}
                            <span>{trendValue}</span>
                        </div>
                    )}
                </div>
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                </div>
            </div>
        </div>
    );
};

// Pipeline Stage Card
const PipelineStageCard = ({ stage, count, amount, percentage }) => {
    const stageColors = {
        "Processing": "bg-slate-500",
        "Underwriting": "bg-blue-500",
        "Action Required": "bg-amber-500",
        "Approved": "bg-emerald-500",
        "Cleared to Close": "bg-teal-500",
        "Closing": "bg-purple-500",
        "Funded": "bg-green-500",
        "Fall out": "bg-red-500"
    };

    return (
        <div className="flex items-center justify-between py-3 border-b dark:border-slate-800 border-slate-200 last:border-0">
            <div className="flex items-center gap-3">
                <div className={`w-2 h-8 rounded-full ${stageColors[stage] || "bg-slate-500"}`} />
                <div>
                    <p className="dark:text-white text-slate-900 font-medium text-sm">{stage}</p>
                    <p className="text-xs dark:text-slate-500 text-slate-400">{count} loans</p>
                </div>
            </div>
            <div className="text-right">
                <p className="dark:text-white text-slate-900 font-semibold">{formatCurrency(amount)}</p>
                <p className="text-xs dark:text-slate-400 text-slate-500">{formatPercent(percentage)}</p>
            </div>
        </div>
    );
};

// Booked Loan Row
const BookedLoanRow = ({ loan }) => {
    return (
        <tr className="hover:dark:bg-slate-800/50 hover:bg-slate-50 transition-colors">
            <td className="px-4 py-3">
                <div>
                    <p className="dark:text-white text-slate-900 font-medium">{loan.borrower_name || loan.name}</p>
                    <p className="text-xs dark:text-slate-500 text-slate-400">{loan.id}</p>
                </div>
            </td>
            <td className="px-4 py-3 dark:text-slate-300 text-slate-700">{loan.loan_type}</td>
            <td className="px-4 py-3 dark:text-white text-slate-900 font-semibold">{formatCurrency(loan.loan_amount)}</td>
            <td className="px-4 py-3 dark:text-slate-400 text-slate-500">{loan.loan_officer || "—"}</td>
            <td className="px-4 py-3 dark:text-slate-400 text-slate-500">
                {loan.booked_at ? new Date(loan.booked_at).toLocaleDateString() : "—"}
            </td>
            <td className="px-4 py-3">
                <span className="px-2 py-1 bg-emerald-500/10 text-emerald-500 text-xs font-medium rounded-full">
                    Booked
                </span>
            </td>
        </tr>
    );
};

const ReportsAnalytics = ({ featureFlags = {}, onUpgradePlan }) => {
    // Date range state
    const [datePreset, setDatePreset] = useState("this_month");
    const [dateRange, setDateRange] = useState(getDateRange("this_month"));
    const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

    // Data state
    const [production, setProduction] = useState(null);
    const [pipeline, setPipeline] = useState(null);
    const [funnel, setFunnel] = useState(null);
    const [loanOfficers, setLoanOfficers] = useState(null);
    const [bookedLoans, setBookedLoans] = useState([]);
    const [periods, setPeriods] = useState([]);

    // UI state
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [activeTab, setActiveTab] = useState("overview");
    const [isExporting, setIsExporting] = useState(false);

    // Load data - hooks must be called unconditionally
    const loadData = useCallback(async () => {
        setIsLoading(true);
        setError("");

        try {
            const [prodRes, pipeRes, funnelRes, loRes, bookedRes, periodsRes] = await Promise.all([
                fetchProductionReport(dateRange.start, dateRange.end).catch(() => null),
                fetchPipelineReport(dateRange.start, dateRange.end).catch(() => null),
                fetchFunnelReport(dateRange.start, dateRange.end).catch(() => null),
                fetchLoanOfficerReport(dateRange.start, dateRange.end).catch(() => null),
                fetchBookedLoans(dateRange.start, dateRange.end).catch(() => ({ loans: [] })),
                fetchReportingPeriods().catch(() => ({ periods: [] }))
            ]);

            setProduction(prodRes);
            setPipeline(pipeRes);
            setFunnel(funnelRes);
            setLoanOfficers(loRes);
            setBookedLoans(bookedRes?.loans || bookedRes?.data?.loans || []);
            setPeriods(periodsRes?.periods || periodsRes?.data?.periods || []);
        } catch (err) {
            console.error("Failed to load reports:", err);
            setError("Unable to load reports. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, [dateRange.start, dateRange.end]);

    useEffect(() => {
        // Only load data if the feature is enabled
        if (featureFlags.hasReportsAnalytics) {
            loadData();
        }
    }, [loadData, featureFlags.hasReportsAnalytics]);

    // Feature gate check - placed after all hooks
    if (!featureFlags.hasReportsAnalytics) {
        return (
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center max-w-md">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                        <BarChart3 className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-xl font-bold dark:text-white text-slate-900 mb-2">
                        Reports & Analytics
                    </h2>
                    <p className="dark:text-slate-400 text-slate-500 mb-6">
                        Unlock powerful reporting and analytics with the Ultra plan.
                        Track your production, analyze your pipeline, and make data-driven decisions.
                    </p>
                    <button
                        onClick={onUpgradePlan}
                        className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-purple-500 hover:to-indigo-500 transition-all shadow-lg shadow-purple-900/20"
                    >
                        Upgrade to Ultra
                    </button>
                </div>
            </div>
        );
    }

    // Handle date preset change
    const handlePresetChange = (preset) => {
        setDatePreset(preset);
        if (preset !== "custom") {
            setDateRange(getDateRange(preset));
            setShowCustomDatePicker(false);
        } else {
            setShowCustomDatePicker(true);
        }
    };

    // Handle export
    const handleExport = async (format) => {
        setIsExporting(true);
        try {
            const blob = await exportReport("production", dateRange.start, dateRange.end, format);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `production_report_${dateRange.start}_${dateRange.end}.${format}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Export failed:", err);
        } finally {
            setIsExporting(false);
        }
    };

    // Calculate summary metrics
    const totalProduction = production?.total_amount || 0;
    const totalUnits = production?.total_units || 0;
    const avgLoanSize = totalUnits > 0 ? totalProduction / totalUnits : 0;
    const pullThroughRate = funnel?.pull_through_rate || 0;

    return (
        <div className="flex-1 flex flex-col h-full">
            {/* Hero Header */}
            <div
                className="relative overflow-hidden px-6 py-8"
                style={{
                    background: "linear-gradient(120deg, rgba(139, 92, 246, 0.98) 0%, rgba(15, 23, 42, 0.95) 100%)"
                }}
            >
                {/* Background Pattern */}
                <div
                    className="absolute inset-0 opacity-10"
                    style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}
                />

                <div className="relative max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                                    <BarChart3 className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-2xl md:text-3xl font-bold text-white">
                                        Reports & Analytics
                                    </h1>
                                    <p className="text-purple-200 mt-1">
                                        Production metrics and pipeline analysis
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Date Range Selector */}
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="relative">
                                <select
                                    value={datePreset}
                                    onChange={(e) => handlePresetChange(e.target.value)}
                                    className="appearance-none bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-lg px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-white/30 text-sm"
                                >
                                    {DATE_PRESETS.map((preset) => (
                                        <option key={preset.value} value={preset.value} className="bg-slate-800">
                                            {preset.label}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60 pointer-events-none" />
                            </div>

                            {showCustomDatePicker && (
                                <div className="flex items-center gap-2">
                                    <input
                                        type="date"
                                        value={dateRange.start}
                                        onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                                        className="bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-white/30 text-sm"
                                    />
                                    <span className="text-white/60">to</span>
                                    <input
                                        type="date"
                                        value={dateRange.end}
                                        onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                                        className="bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-white/30 text-sm"
                                    />
                                </div>
                            )}

                            <button
                                onClick={loadData}
                                disabled={isLoading}
                                className="p-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors disabled:opacity-50"
                            >
                                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                            </button>

                            <div className="relative group">
                                <button
                                    disabled={isExporting}
                                    className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors disabled:opacity-50 text-sm"
                                >
                                    <Download className="w-4 h-4" />
                                    Export
                                    <ChevronDown className="w-3 h-3" />
                                </button>
                                <div className="absolute right-0 mt-1 w-32 bg-slate-800 border border-slate-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                                    <button
                                        onClick={() => handleExport('pdf')}
                                        className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 rounded-t-lg"
                                    >
                                        PDF
                                    </button>
                                    <button
                                        onClick={() => handleExport('csv')}
                                        className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 rounded-b-lg"
                                    >
                                        CSV
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3">
                            <p className="text-purple-200 text-xs font-medium">Total Production</p>
                            <p className="text-white text-xl font-bold">{formatCurrency(totalProduction)}</p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3">
                            <p className="text-purple-200 text-xs font-medium">Units Closed</p>
                            <p className="text-white text-xl font-bold">{totalUnits}</p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3">
                            <p className="text-purple-200 text-xs font-medium">Avg Loan Size</p>
                            <p className="text-white text-xl font-bold">{formatCurrency(avgLoanSize)}</p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3">
                            <p className="text-purple-200 text-xs font-medium">Pull-Through Rate</p>
                            <p className="text-white text-xl font-bold">{formatPercent(pullThroughRate)}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="px-6 py-3 border-b dark:border-slate-800 border-slate-200 dark:bg-slate-900/50 bg-white">
                <div className="max-w-7xl mx-auto flex gap-1">
                    {[
                        { id: "overview", label: "Overview", icon: BarChart3 },
                        { id: "pipeline", label: "Pipeline", icon: Activity },
                        { id: "booked", label: "Booked Loans", icon: CheckCircle },
                        { id: "officers", label: "Loan Officers", icon: Users }
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id
                                    ? "bg-purple-600 text-white"
                                    : "dark:text-slate-400 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                                }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto px-6 py-6">
                <div className="max-w-7xl mx-auto">
                    {/* Error */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-400">
                            <AlertCircle className="w-5 h-5 flex-shrink-0" />
                            {error}
                        </div>
                    )}

                    {/* Loading */}
                    {isLoading ? (
                        <div className="flex items-center justify-center py-20">
                            <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
                        </div>
                    ) : (
                        <>
                            {/* Overview Tab */}
                            {activeTab === "overview" && (
                                <div className="space-y-6">
                                    {/* Metric Cards */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                        <MetricCard
                                            title="Total Production"
                                            value={formatCurrency(totalProduction)}
                                            subtitle={`${totalUnits} loans closed`}
                                            icon={DollarSign}
                                            trend="up"
                                            trendValue="+12.5% vs last period"
                                            color="emerald"
                                        />
                                        <MetricCard
                                            title="Active Pipeline"
                                            value={formatCurrency(pipeline?.total_amount || 0)}
                                            subtitle={`${pipeline?.total_count || 0} loans in progress`}
                                            icon={Activity}
                                            color="sky"
                                        />
                                        <MetricCard
                                            title="Pull-Through Rate"
                                            value={formatPercent(pullThroughRate)}
                                            subtitle="Application to funding"
                                            icon={Target}
                                            trend={pullThroughRate > 50 ? "up" : "down"}
                                            trendValue={pullThroughRate > 50 ? "Above target" : "Below target"}
                                            color="purple"
                                        />
                                        <MetricCard
                                            title="Avg Days to Close"
                                            value={production?.avg_days_to_close || "—"}
                                            subtitle="Application to funding"
                                            icon={Clock}
                                            color="amber"
                                        />
                                    </div>

                                    {/* Charts Row */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        {/* Production by Loan Type */}
                                        <div className="dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6">
                                            <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-4 flex items-center gap-2">
                                                <PieChart className="w-5 h-5 text-purple-500" />
                                                Production by Loan Type
                                            </h3>
                                            {production?.by_loan_type ? (
                                                <div className="space-y-3">
                                                    {Object.entries(production.by_loan_type).map(([type, data]) => (
                                                        <div key={type} className="flex items-center justify-between">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-3 h-3 rounded-full bg-purple-500" />
                                                                <span className="dark:text-slate-300 text-slate-700">{type}</span>
                                                            </div>
                                                            <div className="text-right">
                                                                <span className="dark:text-white text-slate-900 font-semibold">
                                                                    {formatCurrency(data.amount)}
                                                                </span>
                                                                <span className="dark:text-slate-500 text-slate-400 text-sm ml-2">
                                                                    ({data.count} loans)
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-center dark:text-slate-500 text-slate-400 py-8">
                                                    No production data available
                                                </p>
                                            )}
                                        </div>

                                        {/* Funnel Metrics */}
                                        <div className="dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6">
                                            <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-4 flex items-center gap-2">
                                                <TrendingUp className="w-5 h-5 text-emerald-500" />
                                                Conversion Funnel
                                            </h3>
                                            {funnel ? (
                                                <div className="space-y-4">
                                                    {[
                                                        { label: "Applications", value: funnel.applications || 0 },
                                                        { label: "Submitted to UW", value: funnel.submitted || 0 },
                                                        { label: "Approved", value: funnel.approved || 0 },
                                                        { label: "Cleared to Close", value: funnel.cleared || 0 },
                                                        { label: "Funded", value: funnel.funded || 0 }
                                                    ].map((stage, index, arr) => {
                                                        const prevValue = index > 0 ? arr[index - 1].value : stage.value;
                                                        const convRate = prevValue > 0 ? (stage.value / prevValue * 100) : 0;
                                                        const widthPercent = arr[0].value > 0 ? (stage.value / arr[0].value * 100) : 0;

                                                        return (
                                                            <div key={stage.label}>
                                                                <div className="flex items-center justify-between text-sm mb-1">
                                                                    <span className="dark:text-slate-300 text-slate-700">{stage.label}</span>
                                                                    <span className="dark:text-white text-slate-900 font-semibold">
                                                                        {stage.value}
                                                                        {index > 0 && (
                                                                            <span className="text-xs dark:text-slate-500 text-slate-400 ml-2">
                                                                                ({formatPercent(convRate)})
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                </div>
                                                                <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                                                    <div
                                                                        className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all"
                                                                        style={{ width: `${widthPercent}%` }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <p className="text-center dark:text-slate-500 text-slate-400 py-8">
                                                    No funnel data available
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Pipeline Tab */}
                            {activeTab === "pipeline" && (
                                <div className="dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6">
                                    <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-4 flex items-center gap-2">
                                        <Activity className="w-5 h-5 text-sky-500" />
                                        Pipeline by Stage
                                    </h3>
                                    {pipeline?.by_stage ? (
                                        <div className="divide-y dark:divide-slate-800 divide-slate-200">
                                            {Object.entries(pipeline.by_stage).map(([stage, data]) => (
                                                <PipelineStageCard
                                                    key={stage}
                                                    stage={stage}
                                                    count={data.count}
                                                    amount={data.amount}
                                                    percentage={(data.amount / (pipeline.total_amount || 1)) * 100}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-center dark:text-slate-500 text-slate-400 py-12">
                                            No pipeline data available for this period
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Booked Loans Tab */}
                            {activeTab === "booked" && (
                                <div className="dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl overflow-hidden">
                                    <div className="px-6 py-4 border-b dark:border-slate-800 border-slate-200">
                                        <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2">
                                            <CheckCircle className="w-5 h-5 text-emerald-500" />
                                            Booked Loans
                                            <span className="ml-2 px-2 py-0.5 bg-emerald-500/10 text-emerald-500 text-sm font-medium rounded-full">
                                                {bookedLoans.length}
                                            </span>
                                        </h3>
                                    </div>
                                    {bookedLoans.length > 0 ? (
                                        <div className="overflow-x-auto">
                                            <table className="w-full">
                                                <thead className="dark:bg-slate-800/50 bg-slate-50">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left text-xs font-semibold dark:text-slate-400 text-slate-500 uppercase tracking-wider">
                                                            Borrower
                                                        </th>
                                                        <th className="px-4 py-3 text-left text-xs font-semibold dark:text-slate-400 text-slate-500 uppercase tracking-wider">
                                                            Loan Type
                                                        </th>
                                                        <th className="px-4 py-3 text-left text-xs font-semibold dark:text-slate-400 text-slate-500 uppercase tracking-wider">
                                                            Amount
                                                        </th>
                                                        <th className="px-4 py-3 text-left text-xs font-semibold dark:text-slate-400 text-slate-500 uppercase tracking-wider">
                                                            Loan Officer
                                                        </th>
                                                        <th className="px-4 py-3 text-left text-xs font-semibold dark:text-slate-400 text-slate-500 uppercase tracking-wider">
                                                            Booked Date
                                                        </th>
                                                        <th className="px-4 py-3 text-left text-xs font-semibold dark:text-slate-400 text-slate-500 uppercase tracking-wider">
                                                            Status
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y dark:divide-slate-800 divide-slate-200">
                                                    {bookedLoans.map((loan) => (
                                                        <BookedLoanRow key={loan.id} loan={loan} />
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="text-center py-12">
                                            <FileText className="w-12 h-12 mx-auto dark:text-slate-600 text-slate-300 mb-3" />
                                            <p className="dark:text-slate-400 text-slate-500">
                                                No booked loans for this period
                                            </p>
                                            <p className="text-sm dark:text-slate-500 text-slate-400 mt-1">
                                                Book funded loans to track them in your reports
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Loan Officers Tab */}
                            {activeTab === "officers" && (
                                <div className="dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl overflow-hidden">
                                    <div className="px-6 py-4 border-b dark:border-slate-800 border-slate-200">
                                        <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2">
                                            <Users className="w-5 h-5 text-purple-500" />
                                            Loan Officer Performance
                                        </h3>
                                    </div>
                                    {loanOfficers?.officers && loanOfficers.officers.length > 0 ? (
                                        <div className="overflow-x-auto">
                                            <table className="w-full">
                                                <thead className="dark:bg-slate-800/50 bg-slate-50">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left text-xs font-semibold dark:text-slate-400 text-slate-500 uppercase tracking-wider">
                                                            Loan Officer
                                                        </th>
                                                        <th className="px-4 py-3 text-left text-xs font-semibold dark:text-slate-400 text-slate-500 uppercase tracking-wider">
                                                            Production
                                                        </th>
                                                        <th className="px-4 py-3 text-left text-xs font-semibold dark:text-slate-400 text-slate-500 uppercase tracking-wider">
                                                            Units
                                                        </th>
                                                        <th className="px-4 py-3 text-left text-xs font-semibold dark:text-slate-400 text-slate-500 uppercase tracking-wider">
                                                            Avg Loan Size
                                                        </th>
                                                        <th className="px-4 py-3 text-left text-xs font-semibold dark:text-slate-400 text-slate-500 uppercase tracking-wider">
                                                            Pull-Through
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y dark:divide-slate-800 divide-slate-200">
                                                    {loanOfficers.officers.map((lo) => (
                                                        <tr key={lo.id} className="hover:dark:bg-slate-800/50 hover:bg-slate-50 transition-colors">
                                                            <td className="px-4 py-3">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                                                                        {lo.name?.charAt(0) || "?"}
                                                                    </div>
                                                                    <span className="dark:text-white text-slate-900 font-medium">{lo.name}</span>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 dark:text-white text-slate-900 font-semibold">
                                                                {formatCurrency(lo.production)}
                                                            </td>
                                                            <td className="px-4 py-3 dark:text-slate-300 text-slate-700">
                                                                {lo.units}
                                                            </td>
                                                            <td className="px-4 py-3 dark:text-slate-300 text-slate-700">
                                                                {formatCurrency(lo.avg_loan_size)}
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <span className={`font-medium ${lo.pull_through >= 50 ? "text-emerald-500" : "text-amber-500"
                                                                    }`}>
                                                                    {formatPercent(lo.pull_through)}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="text-center py-12">
                                            <Users className="w-12 h-12 mx-auto dark:text-slate-600 text-slate-300 mb-3" />
                                            <p className="dark:text-slate-400 text-slate-500">
                                                No loan officer data for this period
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReportsAnalytics;
